import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { SmartFormComponent } from 'ngx-smart-form';
import { SharedModule } from '../_shared/shared.module';
import {
  catchError,
  map,
  mergeMap,
  of,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { PraticaProgressService } from '../_core/services/pratica-progress.service';
import { TitlePercentageComponent } from '../_widgets/title-percentage/title-percentage.component';
import { PraticaSection } from '../pratica/models/pratica-section';
import { ProductService } from '../_core/services/product.service';
import { SagittaScore } from './models/sagitta-score-model';
import { SagittaScoreFactory } from './models/sagitta-score-factory';
import { LevelTableElement } from './models/level-table-element';
import { SCORE_RANGE } from './models/score-range';
import { LabelPipeModule } from '../_shared/label-pipe/label-pipe.module';
import { IconService } from '../_core/services/icon.service';
import { AuthService } from '../_core/services/auth.service';
import { StatoPratica } from '../../interfaces/pratica';
import { SAGITTA_SCORE_CONFIG } from './configs/sagitta-score-config';

@Component({
  selector: 'app-sagitta-score',
  standalone: true,
  imports: [SharedModule, TitlePercentageComponent, LabelPipeModule],
  templateUrl: './sagitta-score.component.html',
  styleUrls: ['./sagitta-score.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SagittaScoreComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren(SmartFormComponent)
  smartFormComponentList?: QueryList<SmartFormComponent>;

  sagittaScore?: SagittaScore;

  private saveSubscription?: Subscription;

  private productId: string | undefined;

  readonly title = 'Score';

  readonly displayedColumns: (keyof LevelTableElement | 'color')[] = [
    'evaluation',
    'range',
    'color',
  ];

  readonly dataSource = SCORE_RANGE;

  constructor(
    public praticaProgressService: PraticaProgressService,
    private productService: ProductService,
    private cdr: ChangeDetectorRef,
    private auth: AuthService,
    iconService: IconService
  ) {
    iconService.getIcons(['info']);
  }

  ngOnInit(): void {
    const idPratica = this.praticaProgressService.selectedPratica?.id;
    if (idPratica) {
      this.subscribeToPraticaEvents();
      this.start(idPratica!);
    }
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    this.saveSubscription?.unsubscribe();
  }

  private start(idPratica: string) {
    this.productService
      .list(idPratica)
      .pipe(
        map((res) => res[0]),
        tap((res) => {
          this.productId = res?.id;
          this.sagittaScore = SagittaScoreFactory.getScore(
            res.sagittaScore,
            SAGITTA_SCORE_CONFIG
          );
          this.cdr.detectChanges();
        }),
        tap(() => {
          this.trackProgress();
          if (
            this.praticaProgressService.isFrozen &&
            !(
              this.auth.isFundManager &&
              this.praticaProgressService.selectedPratica?.state ===
                StatoPratica.COMPLETATA
            )
          ) {
            this.smartFormComponentList?.forEach((fC) => {
              fC.form.disable();
            });
          }
        })
      )
      .subscribe((res) => {
        SagittaScore.subscribeToFormFieldValueChanges(
          this.sagittaScore?.areas || []
        );
        this.cdr.detectChanges();
      });
  }

  save() {
    if (!this.productId || !this.sagittaScore) return of(undefined);
    let s = SagittaScoreFactory.saveScore(this.sagittaScore);
    return this.productService.edit({ sagittaScore: s }, this.productId).pipe(
      tap((_) => {
        this.praticaProgressService.notifySectionUpdate();
      }),
      catchError((err) => {
        console.log(err);
        return of(undefined);
      })
    );
  }

  private subscribeToPraticaEvents() {
    this.saveSubscription = this.praticaProgressService.triggerSave$
      .pipe(
        mergeMap(() => {
          return this.save();
        })
      )
      .subscribe();
  }

  private trackProgress() {
    this.praticaProgressService
      .aggregateProgress(
        this.smartFormComponentList?.toArray() || [],
        PraticaSection.SCORE
      )
      .subscribe();
  }
}
